import React, { useEffect, useState } from 'react';
import { Table, Stack, Divider, Checkbox, DateRangePicker, Tag } from 'rsuite';
import { AxiosError } from 'axios';
import { Link } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import TablePagination from '@/components/TablePagination';
import { BaseErrorModel, PaginationFilterResult } from '@/models/core.model';
import { EnergyHubVisit, EnergyHubVisitPaginationFilter, energyHubTimeZone, formatHkd } from '@/models/energy-hub.model';
import { getPagedEnergyHubVisits } from '@/services/energy-hub.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;

interface Props {
  energyHubId: number;
}

const EnergyHubVisitList: React.FC<Props> = ({ energyHubId }) => {
  const [filterReq, setFilterReq] = useState<EnergyHubVisitPaginationFilter>(new EnergyHubVisitPaginationFilter());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<EnergyHubVisit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [filterReq, energyHubId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getPagedEnergyHubVisits(energyHubId, filterReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching visits: ${em?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="flex-end" spacing={12}>
        <Checkbox checked={!!filterReq.clubOnly}
          onChange={(_, checked) => setFilterReq(pre => ({ ...pre, pageNumber: 1, clubOnly: checked || undefined }))}>
          Club check-ins only
        </Checkbox>
        <DateRangePicker
          placeholder="Checked in between"
          value={filterReq.from && filterReq.to ? [filterReq.from, filterReq.to] : null}
          onChange={range => setFilterReq(pre => ({
            ...pre, pageNumber: 1, from: range?.[0] ?? undefined, to: range?.[1] ?? undefined,
          }))}
        />
      </Stack>
      <Table height={500} data={items} loading={loading}>
        <Column flexGrow={0.5}>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={1.2}>
          <HeaderCell>Checked In (HK)</HeaderCell>
          <Cell fullText>{(r: EnergyHubVisit) => jsDateToZone(r.checkedInAt, energyHubTimeZone)}</Cell>
        </Column>
        <Column flexGrow={1.4} fullText>
          <HeaderCell>Runner</HeaderCell>
          <Cell>{(r: EnergyHubVisit) => <Link to={`/users/${r.userId}`}>{r.userName ?? `#${r.userId}`}</Link>}</Cell>
        </Column>
        <Column flexGrow={0.7} align="center">
          <HeaderCell>Kind</HeaderCell>
          <Cell>{(r: EnergyHubVisit) => (r.clubVisitId ? <Tag color="blue">Club</Tag> : <Tag>Solo</Tag>)}</Cell>
        </Column>
        <Column flexGrow={2} fullText>
          <HeaderCell>Hub Run</HeaderCell>
          <Cell>{(r: EnergyHubVisit) => (r.eventName ? `${r.eventName}${r.clubName ? ` (${r.clubName})` : ''}` : '-')}</Cell>
        </Column>
        <Column flexGrow={0.7} align="right">
          <HeaderCell>Fee</HeaderCell>
          <Cell>{(r: EnergyHubVisit) => (r.fee ? formatHkd(r.fee) : '-')}</Cell>
        </Column>
        <Column flexGrow={0.7} align="right">
          <HeaderCell>Waived</HeaderCell>
          <Cell>{(r: EnergyHubVisit) => (r.waivedFee ? formatHkd(r.waivedFee) : '-')}</Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
        onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
      />
    </PageContent>
  );
};

export default EnergyHubVisitList;
